/**
 * VolumeService — Earthwork & Surface Volume Calculations
 *
 * Supports surveyors & civil engineers for:
 * - Polygon area
 * - Simple prismoidal / average end area volumes
 * - Future: TIN vs Grid, between two surfaces, from raster (COG/DEM)
 */
import area from '@turf/area';
import { polygon as turfPolygon } from '@turf/helpers';
import type { Geometry, Polygon, MultiPolygon } from 'geojson';

export class VolumeService {
  computePolygonArea(geom: Geometry): number {
    if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
      return area({ type: 'Feature', geometry: geom, properties: {} });
    }
    return 0;
  }

  /**
   * Very simple volume = area × average depth.
   * In production this would sample a DEM at many points or use a second surface.
   */
  simplePrismoidalVolume(areaSqm: number, avgDepthMeters: number): number {
    return areaSqm * avgDepthMeters;
  }

  /**
   * Placeholder for future full surface-to-surface volume.
   * Would accept two rasters or two TINs + a boundary.
   */
  async computeSurfaceVolume(params: {
    boundary: Polygon | MultiPolygon;
    surface1?: string; // raster layer id or url
    surface2?: string;
    baseElevation?: number;
  }): Promise<{ cut: number; fill: number; net: number }> {
    // Stub — real implementation would use georaster or PostGIS ST_SummaryStats + ST_Clip
    const a = this.computePolygonArea(params.boundary);
    const base = params.baseElevation ?? 0;
    return {
      cut: a * 1.2,
      fill: a * 0.7,
      net: a * 0.5,
    };
  }
}

export const volumeService = new VolumeService();
